import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { axisTooltipFormatter } from '@common/utils/chartTooltipFormatter';
import { truncateLabel } from '@common/utils/chartUtils';
import ChartWidgetContainer from '@common/components/ChartWidgetContainer';

function TimeseriesWidget({ widget }) {
  const { t } = useTranslation();
  const { data } = widget;

  const option = useMemo(() => {
    if (!data) return null;

    // Data structure:
    // data = {
    //   entityMap: { id: { name, displayName } }, // Entity metadata
    //   timeseries: { id: [{ timestamp, value }] } // Timeseries data per ID
    // }

    const { entityMap = {}, timeseries = {} } = data;

    // Ensure timeseries is an object and extract all unique timestamps from all series
    const allTimestamps = new Set();
    if (timeseries && typeof timeseries === 'object') {
      Object.values(timeseries).forEach((series) => {
        // Ensure series is an array
        if (Array.isArray(series)) {
          series.forEach((point) => {
            if (point && point.timestamp) {
              allTimestamps.add(point.timestamp);
            }
          });
        }
      });
    }

    // Sort timestamps
    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA - dateB;
    });

    // Build series for each entity
    const series = Object.keys(timeseries || {}).map((entityId) => {
      const entity = entityMap[entityId] || {};
      // Use displayName if available (formatted as "host - metric" for metrics), otherwise fall back to name
      const entityName = entity.displayName || entity.name || `Unknown Entity ${entityId}`;
      const seriesData = timeseries[entityId];
      
      // Handle different data structures:
      // 1. Array of {timestamp, value} objects
      // 2. Object with nested data/timeseries/values array
      // 3. Object with timestamp/value properties directly
      let seriesArray = [];
      
      if (Array.isArray(seriesData)) {
        // Direct array of points
        seriesArray = seriesData;
      } else if (seriesData && typeof seriesData === 'object') {
        // Try to find timeseries array in common fields
        seriesArray = seriesData.data || seriesData.timeseries || seriesData.values || [];
        
        // If still not an array, check if the object itself has timestamp/value
        if (!Array.isArray(seriesArray) && seriesData.timestamp !== undefined && seriesData.value !== undefined) {
          // Single data point
          seriesArray = [seriesData];
        }
      }
      
      // Ensure seriesArray is an array
      if (!Array.isArray(seriesArray)) {
        seriesArray = [];
      }
      
      // Create a map of timestamp -> value for quick lookup
      const valueMap = new Map();
      seriesArray.forEach((point) => {
        if (point && typeof point === 'object') {
          // Handle different point formats
          const timestamp = point.timestamp || point.time || point.ts || point.date;
          const { value: pointValue, data: pointData } = point;
          let value = pointValue;
          if (value === undefined) {
            value = pointData !== undefined ? pointData : null;
          }
          
          if (timestamp != null && value !== null && value !== undefined) {
            valueMap.set(timestamp, value);
          }
        }
      });

      // Map sorted timestamps to values (null if no data for that timestamp)
      const values = sortedTimestamps.map((timestamp) => (valueMap.has(timestamp) ? valueMap.get(timestamp) : null));

      return {
        name: entityName,
        type: 'line',
        data: values,
        connectNulls: false, // Don't connect missing data points
        smooth: true,
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        confine: true,
        appendToBody: true,
        axisPointer: {
          type: 'cross',
        },
        formatter: axisTooltipFormatter,
      },
      legend: {
        data: series.map((s) => s.name),
        textStyle: {
          fontSize: 11,
        },
        formatter: (name) => truncateLabel(name),
        tooltip: {
          show: true,
          formatter: (params) => params.name,
        },
        type: 'scroll',
        bottom: 0,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: sortedTimestamps,
        axisLabel: {
          fontSize: 11,
          rotate: 45,
          formatter: (value) => {
            try {
              const date = new Date(value);
              return date.toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              });
            } catch {
              return String(value);
            }
          },
        },
        tooltip: {
          show: true,
          formatter: (params) => params.value,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 11,
        },
      },
      series: series.map((s, index) => ({
        ...s,
        itemStyle: {
          color: s.itemStyle?.color || `hsl(${index * 60}, 70%, 50%)`,
        },
        lineStyle: {
          color: s.lineStyle?.color || `hsl(${index * 60}, 70%, 50%)`,
        },
      })),
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          type: 'slider',
          start: 0,
          end: 100,
          height: 20,
        },
      ],
    };
  }, [data]);

  return (
    <ChartWidgetContainer
      widget={widget}
      option={option}
      noDataMessage={data === null ? t('dashboard.widgets.timeseries.noEntitiesSelected') : undefined}
    />
  );
}

export default TimeseriesWidget;
